/**
 * Mirror of `evaluateFeatureFlag` / `classifyFeatureFlagChange` from `@elios-backend/common` —
 * one pure function, implemented identically on the backend, iOS, Android and here, and pinned
 * by the shared vectors (`tests/fixtures/feature-flag-evaluation.json`, copied verbatim).
 *
 * "Available" always means the feature is usable. The console evaluates to show what a platform
 * sees, and classifies a pending write to mirror the backend's superAdmin rule; the backend is
 * the authority for both.
 */
import type { AppPlatform } from "@/firestore/appVersionPolicies";
import type { FeatureFlagRiskyDirection } from "./registry";

/** The targeting half of a flag row. Modes are strings: unknown values behave as `off`. */
export interface FeatureFlagTargeting {
  iosMode: string;
  iosMinBuild: number | null;
  iosMaxBuild: number | null;
  androidMode: string;
  androidMinBuild: number | null;
  androidMaxBuild: number | null;
}

/** Everything evaluation reads from a flag row. */
export interface FeatureFlagSnapshot extends FeatureFlagTargeting {
  message: string | null;
  expectedBackAt: string | null;
}

/** A client identified by its platform headers; `null` = unknown / malformed (server only). */
export interface FeatureFlagClient {
  platform: AppPlatform;
  build: number;
}

export type FeatureAvailability =
  | { result: "available" }
  | { result: "unavailable"; message: string | null; expectedBackAt: string | null };

export interface FeatureFlagChange {
  /** Some platform loses builds, or the unknown-client outcome goes available → unavailable. */
  disables: boolean;
  /** Some platform gains builds, or the unknown-client outcome goes unavailable → available. */
  enables: boolean;
}

const AVAILABLE: FeatureAvailability = { result: "available" };

function unavailable(flag: FeatureFlagSnapshot | null): FeatureAvailability {
  return {
    result: "unavailable",
    message: flag?.message ?? null,
    expectedBackAt: flag?.expectedBackAt ?? null,
  };
}

function platformTargeting(
  flag: FeatureFlagTargeting,
  platform: AppPlatform,
): { mode: string; min: number | null; max: number | null } {
  return platform === "ios"
    ? { mode: flag.iosMode, min: flag.iosMinBuild, max: flag.iosMaxBuild }
    : { mode: flag.androidMode, min: flag.androidMinBuild, max: flag.androidMaxBuild };
}

/** CONTRACT §3: no row → code default; unknown client → available only when both platforms are `on`. */
export function evaluateFeatureFlag(
  flag: FeatureFlagSnapshot | null,
  defaultAvailable: boolean,
  client: FeatureFlagClient | null,
): FeatureAvailability {
  if (flag === null) return defaultAvailable ? AVAILABLE : unavailable(null);
  if (client === null) return unknownClientAvailable(flag) ? AVAILABLE : unavailable(flag);
  const { mode, min, max } = platformTargeting(flag, client.platform);
  switch (mode) {
    case "on":
      return AVAILABLE;
    case "builds":
      return (min === null || client.build >= min) && (max === null || client.build <= max)
        ? AVAILABLE
        : unavailable(flag);
    default:
      // `off` and any mode this console does not know.
      return unavailable(flag);
  }
}

function unknownClientAvailable(flag: FeatureFlagTargeting): boolean {
  return flag.iosMode === "on" && flag.androidMode === "on";
}

/** The set of builds that evaluate available on one platform: an inclusive interval, or empty. */
type BuildInterval = { lo: number; hi: number } | null;

function availableBuilds(flag: FeatureFlagTargeting, platform: AppPlatform): BuildInterval {
  const { mode, min, max } = platformTargeting(flag, platform);
  if (mode === "on") return { lo: 0, hi: Infinity };
  if (mode !== "builds") return null;
  const lo = min ?? 0;
  const hi = max ?? Infinity;
  return lo > hi ? null : { lo, hi };
}

/** `a ⊆ b`, with ∅ a subset of anything. */
function isSubset(a: BuildInterval, b: BuildInterval): boolean {
  if (a === null) return true;
  return b !== null && b.lo <= a.lo && a.hi <= b.hi;
}

/**
 * CONTRACT §3.1: compares the availability of two targetings, per platform plus the unknown
 * client. Message / `expectedBackAt` edits are neither a disable nor an enable.
 */
export function classifyFeatureFlagChange(
  before: FeatureFlagTargeting,
  after: FeatureFlagTargeting,
): FeatureFlagChange {
  const platforms: AppPlatform[] = ["ios", "android"];
  const unknownBefore = unknownClientAvailable(before);
  const unknownAfter = unknownClientAvailable(after);
  return {
    disables:
      platforms.some(
        (platform) =>
          !isSubset(availableBuilds(before, platform), availableBuilds(after, platform)),
      ) ||
      (unknownBefore && !unknownAfter),
    enables:
      platforms.some(
        (platform) =>
          !isSubset(availableBuilds(after, platform), availableBuilds(before, platform)),
      ) ||
      (!unknownBefore && unknownAfter),
  };
}

/** The targeting a key behaves as when it has no row: both `on`, or both `off`, bounds null. */
export function defaultTargeting(defaultAvailable: boolean): FeatureFlagTargeting {
  const mode = defaultAvailable ? "on" : "off";
  return {
    iosMode: mode,
    iosMinBuild: null,
    iosMaxBuild: null,
    androidMode: mode,
    androidMinBuild: null,
    androidMaxBuild: null,
  };
}

/** The backend's superAdmin rule for a classified change. */
export function requiresSuperAdmin(
  riskyDirection: FeatureFlagRiskyDirection,
  change: FeatureFlagChange,
): boolean {
  return (
    (riskyDirection === "disable" && change.disables) ||
    (riskyDirection === "enable" && change.enables)
  );
}
